/**
 * 🐑 **BODIES THAT BUMP INTO EACH OTHER — T274 and T262, 2026-09-25.**
 *
 * 🎯 **haugur, 2026-09-24:** *"a sheep must block you."* 📏 **And the flock
 * stood inside itself**: nearest-neighbour median **0.4 m** against a body
 * **1.07 m** long (T262). **Both had one cause: nothing in this game knew that
 * one body was in another's way.** Blocks stopped everything; bodies stopped
 * nothing.
 *
 * ## 🔴 THE RULE, AND WHY IT IS NOT MINECRAFT'S
 *
 * 📖 **Minecraft gives a mob NO solid box at all** —
 * `Entity.getCollisionBoundingBox()` returns `null`; only a boat or a shulker is
 * solid. Two overlapping bodies are merely **shoved apart, 0.05 a tick**
 * (`applyEntityCollision`, read off the decompiled 1.12 source). **So in
 * Minecraft you walk half into a sheep and it slides off you.** That is not
 * *"block you"*.
 *
 * ✅ **Ours, agreed with haugur 2026-09-25 — *solid, but it yields*:**
 *
 * | | |
 * |---|---|
 * | 🧍 **A sheep is solid TO YOU** | the browser stops you at its edge, exactly like a wall — {@link blockedByBulks} |
 * | 🐑 **A sheep you PRESS gives way** | the server moves it away from you at {@link SHOVE_SPEED} — {@link crowdShoves} |
 * | 🐑🐑 **Two sheep push APART** | half the overlap each, per tick, capped — T262 |
 *
 * 🔴 **THE SECOND LINE IS THE WAY OUT.** A sheep that never moves is a wall, and
 * five of them round you is a cell. **Because a pressed sheep yields, you can
 * always shoulder your way out** — and nothing a sheep does can be refused by
 * the ground: every shove goes through the species' own `fits`, so **you cannot
 * push a sheep through a fence, into a wall or into the sea.** 👉 *That is what
 * makes a pen a pen.*
 *
 * ⚠️ **Why the split between browser and server is safe:** the server does not
 * check player movement at all (`index.ts` takes `player_state` as given), so
 * **a browser-only block cannot rubber-band anybody.** The handoff said it
 * would, and it was wrong.
 *
 * 📐 **Boxes are SQUARE and upright**, for the reason `withFootprint` gives: a
 * rectangle would have to turn with the animal, and *can I be here* would
 * depend on which way it faces. 🔴 **A sheep's box is `SHEEP_WIDTH`, the SAME
 * number its footprint against walls uses** — one answer to *how big is a
 * sheep for bumping into things*, never two.
 */

import { seedFrom, type Fits, type Ground, type Vec3 } from './creature.ts';

/**
 * 🧍 **How wide the player is**, as a half-width in metres.
 *
 * ⚠️ **Slightly less than a third of a block on each side**, so a one-block gap
 * is comfortably walkable and you do not catch on a corner you are clearly past.
 *
 * 🔴 **Moved here from the client's `movement.ts` for T274** — the server now
 * needs to know how big you are, to know when you are pressing a sheep, and a
 * second copy of the player's size is the fault this repo names oftener than
 * any other. `movement.ts` re-exports it.
 */
export const PLAYER_RADIUS = 0.3;

/**
 * 🧍 **How tall the player is**, in metres — a little above the eye.
 *
 * ⚠️ **Taller than `EYE_HEIGHT` (1.7) on purpose.** The top of a head is above
 * the eyes, and a player who can walk into a gap their eyes fit through but
 * their head does not is a player clipping through the ceiling.
 */
export const PLAYER_HEIGHT = 1.8;

/**
 * 📦 **One body, as a box standing on its feet.** `x, y, z` is the middle of the
 * FEET — the same point a creature's `at` and the player's position are.
 */
export interface Bulk {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Half the width, metres. The box is square. */
  readonly half: number;
  readonly height: number;
}

/** 🧍 The player's box, standing at `at`. */
export function playerBulk(at: Vec3): Bulk {
  return { x: at.x, y: at.y, z: at.z, half: PLAYER_RADIUS, height: PLAYER_HEIGHT };
}

/**
 * ⚠️ **How far two boxes must reach into each other to count as touching.**
 * Without it, a body standing exactly against another — which is where
 * {@link blockedByBulks} leaves you — would be "inside" it through rounding.
 */
const SKIN = 0.001;

/** 📐 How deep two boxes are inside each other, flat, or 0 if they are not. */
function depth(a: Bulk, b: Bulk): number {
  if (a.y + a.height <= b.y + SKIN || b.y + b.height <= a.y + SKIN) return 0;
  const reach = a.half + b.half;
  const inX = reach - Math.abs(a.x - b.x);
  const inZ = reach - Math.abs(a.z - b.z);
  if (inX <= SKIN || inZ <= SKIN) return 0;
  /* The shallower axis is how far one must move to clear the other. */
  return Math.min(inX, inZ);
}

/** 🧱 Do these two boxes overlap at all? Touching is not overlapping. */
export function bulksOverlap(a: Bulk, b: Bulk): boolean {
  return depth(a, b) > 0;
}

/**
 * 🧍 **May a body move from `from` to `to`, with these others in the way?**
 *
 * `true` means **blocked** — the same sense as a wall.
 *
 * 🔴 **And a body ALREADY inside another may only move AWAY from it.** The
 * wall rule in `controls.ts` lets somebody stuck inside a block walk out in any
 * direction; here the sheep is right there and its centre is known, so the
 * honest version is sharper: *out, not further in.* ⚠️ **Refusing every
 * direction would trap anybody a sheep walked into**, which is the ordinary
 * case — the server moves sheep, and a sheep can step into you.
 */
export function blockedByBulks(
  self: { readonly half: number; readonly height: number },
  from: Vec3,
  to: Vec3,
  others: readonly Bulk[],
): boolean {
  const there: Bulk = { ...to, ...self };
  const here: Bulk = { ...from, ...self };
  for (const other of others) {
    if (!bulksOverlap(there, other)) continue;
    if (!bulksOverlap(here, other)) return true;
    const was = Math.hypot(from.x - other.x, from.z - other.z);
    const now = Math.hypot(to.x - other.x, to.z - other.z);
    if (now <= was) return true;
  }
  return false;
}

/**
 * 🐑 **How fast a pressed sheep gives way, metres a second.**
 *
 * 📖 **Minecraft's shove is 0.05 a tick at 20 ticks a second**, and against
 * ground drag of 0.546 that settles near **1.2 m/s** — which is where this came
 * from. ⚠️ **A DIAL, and not haugur's number.** It is how fast you can herd a
 * sheep by walking into it: at `WALK_SPEED` 5.6 you are slowed to this while
 * you push, **so a sheep costs you something to move and never stops you dead.**
 */
export const SHOVE_SPEED = 1.2;

/**
 * 🤏 **How near a player must be to count as PRESSING a sheep**, metres, past
 * touching.
 *
 * 🔴 **It cannot be zero.** The browser stops you exactly AT the sheep's edge,
 * so on the server your box and its box only ever touch — and touching is not
 * overlapping. ⚠️ **It also covers the lag**: the server hears where you are
 * five times a second, so its idea of you trails the truth a little.
 */
export const PRESS_REACH = 0.15;

/**
 * 🐑🐑 **How fast two overlapping creatures separate, metres a second EACH** —
 * T262.
 *
 * ⚠️ **Each moves half the overlap per tick, but never faster than this**, so a
 * pair born on top of each other drifts apart over a second rather than
 * jumping — a jump is a teleport by another name.
 */
export const SETTLE_SPEED = 1.5;

/** One body the crowd pass moves: a creature with a box, and its id. */
export interface Crowded {
  readonly id: string;
  readonly bulk: Bulk;
}

/**
 * 🧮 **How far each creature should be shoved this tick**, before the ground
 * has a say. Index for index with `bodies`.
 *
 * 🔴 **Pure arithmetic, and the ground is NOT asked here** — {@link shoveOnGround}
 * does that, one axis at a time, exactly as `stepBody` does a walk. Splitting
 * them is what lets a test say *"these two separate"* without a world.
 *
 * ⚠️ **Players push; nothing pushes a player.** The browser owns where you are
 * and the server does not correct it — so the server shoving you would be a
 * number nobody reads.
 *
 * ⚠️ **Every pair is checked: O(n²).** 📏 Ten sheep per player is 45 pairs; five
 * hundred kept sheep would be 125,000 box tests ten times a second, which the
 * server does not notice. **If pens grow past that, bucket by cell first.**
 */
export function crowdShoves(
  bodies: readonly Crowded[],
  pushers: readonly Bulk[],
  dt: number,
): { dx: number; dz: number }[] {
  const out = bodies.map(() => ({ dx: 0, dz: 0 }));

  /* 🐑🐑 Creature against creature — each gives half. */
  const most = SETTLE_SPEED * dt;
  for (let i = 0; i < bodies.length; i++) {
    const a = bodies.at(i);
    if (a === undefined) continue;
    for (let j = i + 1; j < bodies.length; j++) {
      const b = bodies.at(j);
      if (b === undefined) continue;
      const deep = depth(a.bulk, b.bulk);
      if (deep === 0) continue;
      const [ux, uz] = apart(a, b);
      const move = Math.min(deep / 2, most);
      const oa = out.at(i);
      const ob = out.at(j);
      if (oa === undefined || ob === undefined) continue;
      oa.dx -= ux * move;
      oa.dz -= uz * move;
      ob.dx += ux * move;
      ob.dz += uz * move;
    }
  }

  /* 🧍 A player pressing a creature — only the creature moves. */
  const shove = SHOVE_SPEED * dt;
  for (let i = 0; i < bodies.length; i++) {
    const body = bodies.at(i);
    const o = out.at(i);
    if (body === undefined || o === undefined) continue;
    for (const pusher of pushers) {
      const reaching: Bulk = { ...pusher, half: pusher.half + PRESS_REACH };
      if (depth(reaching, body.bulk) === 0) continue;
      const [ux, uz] = direction(pusher.x, pusher.z, body.bulk.x, body.bulk.z, body.id);
      o.dx += ux * shove;
      o.dz += uz * shove;
    }
  }
  return out;
}

/** The unit direction from `a` to `b`, flat. */
function apart(a: Crowded, b: Crowded): [number, number] {
  return direction(a.bulk.x, a.bulk.z, b.bulk.x, b.bulk.z, a.id + b.id);
}

/**
 * ➡️ **The unit direction from one point to another, flat.**
 *
 * 🔴 **Two bodies at EXACTLY the same point still get a direction.** Minecraft
 * skips a pair closer than 0.01 — so two mobs spawned on one spot stay fused for
 * ever. ✅ **Ours picks an angle from the ids**, seeded rather than random (D2),
 * so the same pair always splits the same way and a replay is a replay.
 */
function direction(fx: number, fz: number, tx: number, tz: number, key: string): [number, number] {
  const dx = tx - fx;
  const dz = tz - fz;
  const length = Math.hypot(dx, dz);
  if (length > 1e-6) return [dx / length, dz / length];
  const angle = (seedFrom(key) / 0x100000000) * Math.PI * 2;
  return [Math.cos(angle), Math.sin(angle)];
}

/**
 * 🧱 **Apply a shove, and let the ground refuse it** — x, then z, each undone
 * alone, exactly as `stepBody` walks.
 *
 * 🔴 **This is what makes a pen hold.** A shove is a request, not a teleport:
 * the species' own `fits` — *not in the sea, not in a wall, not on a roof,
 * something solid underfoot* — gets the last word. **So a player can herd a
 * sheep along a fence and never through it.**
 */
export function shoveOnGround(
  at: Vec3,
  shove: { readonly dx: number; readonly dz: number },
  ground: Ground,
  fits: Fits,
): Vec3 {
  let now = at;
  if (shove.dx !== 0) {
    const tryX = { x: now.x + shove.dx, y: now.y, z: now.z };
    if (fits(ground, tryX)) now = tryX;
  }
  if (shove.dz !== 0) {
    const tryZ = { x: now.x, y: now.y, z: now.z + shove.dz };
    if (fits(ground, tryZ)) now = tryZ;
  }
  return now;
}
